import { useNavigate } from "react-router-dom";
import { getApiUrl } from "../utils/api";
import { useState, useContext, useEffect, useRef } from "react";
import {
  FaRegBookmark,
  FaBookmark,
  FaRegComment,
  FaEye,
  FaEllipsisH,
} from "react-icons/fa";
import { contentToHtml, htmlToPlainText } from "../lib/contentToHtml";
import BadgeIcon from "./BadgeIcon";
import LikeHeart, { LIKE_BURST_MS } from "./LikeHeart";
import { useToast } from "../context/ToastContext";
import { UserContext } from "../context/UserContext";
import { getAvatarUrl, getPoemImageUrl } from "../utils/avatar";
import { getCommentCount } from "../utils/poemStats";
import "./PoemCard.css";

const ACCENT = "#f26522";
const STAT_MUTED = "#999999";

function PoemCard({
  poem,
  initialSaved = false,
  onSavedChange,
  showSave = true,
  saveAfterThumbnail = false,
  onLikeChange,
}) {
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useContext(UserContext);
  const [saving, setSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(initialSaved);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const [isLiked, setIsLiked] = useState(
    user && poem.likes && Array.isArray(poem.likes)
      ? poem.likes.some((like) => String(like._id || like) === String(user._id))
      : false
  );
  const [likeCount, setLikeCount] = useState(poem.likes?.length || 0);
  const [likeAnimating, setLikeAnimating] = useState(false);

  useEffect(() => {
    if (user && poem.likes && Array.isArray(poem.likes)) {
      setIsLiked(
        poem.likes.some((like) => String(like._id || like) === String(user._id))
      );
    } else {
      setIsLiked(false);
    }
    setLikeCount(poem.likes?.length || 0);
  }, [poem.likes, user]);

  useEffect(() => {
    if (likeAnimating) {
      const timeout = setTimeout(() => setLikeAnimating(false), LIKE_BURST_MS);
      return () => clearTimeout(timeout);
    }
  }, [likeAnimating]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const close = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [menuOpen]);

  const getAvatar = (u) => getAvatarUrl(u?.avatar);

  const getPrimaryAuthor = () => {
    if (poem?.author?.username) return poem.author;
    if (Array.isArray(poem.collaborators) && poem.collaborators[0]) {
      return poem.collaborators[0];
    }
    return null;
  };

  const EXCERPT_LIMIT = 220;
  const plainText = htmlToPlainText(contentToHtml(poem.content));
  const isTruncated = plainText.length > EXCERPT_LIMIT;
  const excerpt = isTruncated
    ? `${plainText.slice(0, EXCERPT_LIMIT)}...`
    : plainText;

  const author = getPrimaryAuthor();
  const dateLabel = new Date(poem.createdAt).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  const handleLike = async (e) => {
    e.stopPropagation();
    if (!user) {
      toast.error("Please log in to like poems", { title: "Login Required" });
      return;
    }
    if (likeAnimating) return;

    const previousLiked = isLiked;
    const previousCount = likeCount;
    const willLike = !previousLiked;
    if (willLike) setLikeAnimating(true);
    setIsLiked(!isLiked);
    setLikeCount(previousLiked ? likeCount - 1 : likeCount + 1);

    try {
      const res = await fetch(getApiUrl(`/api/poems/${poem._id}/like`), {
        method: "PUT",
        credentials: "include",
      });
      if (!res.ok) {
        setIsLiked(previousLiked);
        setLikeCount(previousCount);
        setLikeAnimating(false);
        throw new Error("Failed to like poem");
      }
      const updated = await res.json();
      setIsLiked(
        updated.likes && Array.isArray(updated.likes)
          ? updated.likes.some((like) => String(like._id || like) === String(user._id))
          : false
      );
      setLikeCount(updated.likes?.length || 0);
      if (typeof onLikeChange === "function") {
        try {
          onLikeChange(updated, poem);
        } catch {
          /* ignore */
        }
      }
    } catch (error) {
      console.error("Error liking poem:", error);
      toast.error("Failed to like poem");
      setLikeAnimating(false);
    }
  };

  const menu = (
    <div
      className="poem-card__menu-wrap"
      ref={menuRef}
    >
      <button
        type="button"
        className="poem-card__menu-btn"
        aria-label="Poem options"
        onClick={(e) => {
          e.stopPropagation();
          setMenuOpen((v) => !v);
        }}
      >
        <FaEllipsisH />
      </button>
      {menuOpen && (
        <ul className="poem-card__menu-dropdown">
          <li>
            <button
              type="button"
              className="w-full text-left px-3 py-2 hover:bg-[#fff5ef]"
              onClick={(e) => {
                e.stopPropagation();
                setMenuOpen(false);
                navigate(`/poems/${poem._id}`);
              }}
            >
              View poem
            </button>
          </li>
          {author?.username && (
            <li>
              <button
                type="button"
                className="w-full text-left px-3 py-2 hover:bg-[#fff5ef]"
                onClick={(e) => {
                  e.stopPropagation();
                  setMenuOpen(false);
                  navigate(`/users/${author.username}`);
                }}
              >
                View author
              </button>
            </li>
          )}
        </ul>
      )}
    </div>
  );

  const saveInStats = showSave && !saveAfterThumbnail;
  const saveInMedia = showSave && saveAfterThumbnail;

  const saveButton = (
    <button
      type="button"
      className={
        saveInMedia ? "poem-card__save-btn" : "poem-card__stat poem-card__stat--save"
      }
      style={
        saveInMedia
          ? undefined
          : {
              background: "none",
              border: "none",
              cursor: saving ? "wait" : "pointer",
              padding: 0,
            }
      }
      disabled={saving}
      onClick={async (e) => {
        e.stopPropagation();
        if (saving) return;
        setSaving(true);
        try {
          const res = await fetch(
            getApiUrl(`/api/poems/${poem._id}/save`),
            { method: "PUT", credentials: "include" }
          );
          if (res.ok) {
            const data = await res.json();
            const nextSaved = Boolean(data.saved);
            setIsSaved(nextSaved);
            if (typeof onSavedChange === "function") {
              try {
                onSavedChange(nextSaved, poem);
              } catch {
                /* ignore */
              }
            }
            toast.success(nextSaved ? "Poem saved" : "Poem unsaved", {
              title: nextSaved ? "Saved" : "Unsaved",
            });
          }
        } catch {
          toast.error("Failed to save poem");
        } finally {
          setSaving(false);
        }
      }}
      aria-label={isSaved ? "Unsave poem" : "Save poem"}
    >
      {isSaved ? (
        <FaBookmark color={ACCENT} size={14} />
      ) : (
        <FaRegBookmark color={STAT_MUTED} size={14} />
      )}
    </button>
  );

  return (
    <article
      className="poem-card"
      onClick={() => navigate(`/poems/${poem._id}`)}
    >
      <div className="poem-card__main">
        <h2 className="poem-card__title">{poem.title}</h2>
        <div
          className={`poem-card__excerpt-wrap${
            isTruncated ? "" : " poem-card__excerpt-wrap--spaced"
          }`}
        >
          <p className="poem-card__excerpt">{excerpt}</p>
          {isTruncated && (
            <button
              type="button"
              className="poem-card__read-more"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/poems/${poem._id}`);
              }}
            >
              Read more
            </button>
          )}
        </div>

        <div className="poem-card__author-row">
          {author && (
            <>
              <img src={getAvatar(author)} alt="" />
              <span
                className="poem-card__author-name inline-flex items-center gap-1"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/users/${author.username}`);
                }}
              >
                {author.username}
                {author.badge && <BadgeIcon type={author.badge} />}
              </span>
              <span aria-hidden="true">·</span>
            </>
          )}
          <span>{dateLabel}</span>
        </div>

        <div className="poem-card__stats">
          <button
            type="button"
            className={`poem-card__stat ${isLiked ? "poem-card__stat--liked" : ""}`}
            style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
            onClick={handleLike}
            aria-label={isLiked ? "Unlike poem" : "Like poem"}
          >
            <LikeHeart
              liked={isLiked}
              animating={likeAnimating}
              size={14}
              likedColor={ACCENT}
              unlikedColor={STAT_MUTED}
            />
            <span>{likeCount}</span>
          </button>
          <span className="poem-card__stat">
            <FaRegComment size={14} color={STAT_MUTED} />
            <span>{getCommentCount(poem)}</span>
          </span>
          <span className="poem-card__stat" title="Views">
            <FaEye size={14} color={STAT_MUTED} />
            <span>{poem.viewCount ?? 0}</span>
          </span>
          {saveInStats && saveButton}
        </div>
      </div>

      <div
        className={`poem-card__media${
          saveInMedia
            ? " poem-card__media--with-save"
            : poem.coverImage
            ? ""
            : " poem-card__media--menu-only"
        }`}
      >
        {poem.coverImage && (
          <img
            src={getPoemImageUrl(poem.coverImage)}
            alt=""
            className="poem-card__cover"
          />
        )}
        {saveInMedia && saveButton}
        {menu}
      </div>
    </article>
  );
}

export default PoemCard;
